/**
 * フィードバック生成の待機中に表示する読み物。
 *
 * 「めんれん先輩からのアドバイス」は5つの評価軸（構成・論理／具体性／志望動機／自己理解／表現力）に対応させ、
 * 文面は docs/CHARACTER.md の口調・言葉遣いガイドラインに従ってめんれん先輩の口調で書く。
 * 一人称は「俺」、二人称は「お前」、語尾は「〜だ」「〜だろ」「〜しろ」「〜だな」「〜じゃねえか」、
 * 敬語と絵文字は使わない。待機中のユーザーは不安な状態なので、厳しさはX投稿より抑えめにする。
 * 「頑張ってください」「すごい」「〜だと思います」のような避けるフレーズは使わない。
 *
 * 「偉人の言葉」は実在の人物の言葉であり、口調を変えると発言の捏造になるため原文のまま載せる。
 * 権利関係の判断を実装者に委ねないため、没後長い人物の広く知られた短い格言に限る。
 * 存命の人物および現代の書籍・記事からは引用しない。
 */
export interface WaitingTip {
  id: string;
  kind: "tip" | "quote";
  /** 2行に収まるよう WAITING_TIP_MAX_LENGTH 文字以内にする。 */
  body: string;
  /** 偉人の言葉のときだけ人物名を持つ。 */
  source?: string;
}

/** 本文の上限文字数。カードの高さを2行ぶんに固定するための制約である。 */
export const WAITING_TIP_MAX_LENGTH = 60;

export const WAITING_TIP_KIND_LABELS: Record<WaitingTip["kind"], string> = {
  tip: "めんれん先輩からのアドバイス",
  quote: "偉人の言葉",
};

/**
 * 読み物を表示する順番をシャッフルして返す。
 *
 * 配列は種類ごとにまとめて並んでいるため、そのまま順送りにすると1回の待機で
 * 片方の種類しか出ない（14〜28秒の待機だと約7〜8割がそうなる）。
 * 表示開始時に順番をシャッフルすることで、アドバイスと偉人の言葉が混ざって出る。
 *
 * 毎回その場でランダムに選ぶ方式にはしない。それだと同じ読み物が近い間隔で
 * 再登場しうるため、シャッフルした順番を一巡させる形にしている。
 *
 * @param random 0以上1未満の乱数を返す関数（テストから差し替えられるようにする）
 */
export function shuffledTipOrder(
  random: () => number = Math.random,
): number[] {
  const order = WAITING_TIPS.map((_, index) => index);

  // Fisher-Yates
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }

  return order;
}

export const WAITING_TIPS: readonly WaitingTip[] = [
  // ── 構成・論理 ──
  {
    id: "tip-conclusion-first",
    kind: "tip",
    body: "結論を先に言え。理由は後だ。それだけで面接官はお前の話を追える。",
  },
  {
    id: "tip-answer-length",
    kind: "tip",
    body: "1問は60秒で収めろ。長く話すほど要点はぼやけるもんだ。",
  },
  {
    id: "tip-ask-why-thrice",
    kind: "tip",
    body: "自分の回答に「なぜ」を3回ぶつけてみろ。だんだん筋が通ってくる。",
  },
  // ── 具体性 ──
  {
    id: "tip-use-numbers",
    kind: "tip",
    body: "「頑張りました」じゃ何も伝わらん。「3か月で20%改善した」だ。",
  },
  {
    id: "tip-star-order",
    kind: "tip",
    body: "状況、課題、やったこと、結果。この順で並べろ。それで話は通る。",
  },
  {
    id: "tip-add-scale",
    kind: "tip",
    body: "固有名詞と規模を一つ入れろ。それだけで話の解像度が変わる。",
  },
  // ── 志望動機 ──
  {
    id: "tip-motivation-two-layers",
    kind: "tip",
    body: "志望動機は2段だ。なぜこの業界か、なぜこの会社か。用意しとけ。",
  },
  {
    id: "tip-what-you-give",
    kind: "tip",
    body: "「成長したい」で止めるな。お前が何を返せるかまで言い切れ。",
  },
  {
    id: "tip-connect-experience",
    kind: "tip",
    body: "会社を選んだ理由は、自分の経験と一本の線でつなげ。",
  },
  // ── 自己理解 ──
  {
    id: "tip-weakness-with-action",
    kind: "tip",
    body: "弱みは自覚と対処をセットで話せ。それができてれば十分だ。",
  },
  {
    id: "tip-explain-past-choices",
    kind: "tip",
    body: "過去の選択を「なぜそうした」で語れるか。そこが自己理解だ。",
  },
  {
    id: "tip-collect-others-words",
    kind: "tip",
    body: "他人からよく言われる言葉を集めろ。それがお前の強みの種だ。",
  },
  // ── 表現力 ──
  {
    id: "tip-finish-sentences",
    kind: "tip",
    body: "語尾を言い切れ。同じ内容でも自信があるように聞こえるもんだ。",
  },
  {
    id: "tip-breathe-between",
    kind: "tip",
    body: "早口になったら一文ごとに息を継げ。それだけで聞き取れる。",
  },
  {
    id: "tip-warm-up-voice",
    kind: "tip",
    body: "本番前に声を出しておけ。最初の一言が詰まらなくなるぞ。",
  },

  // ── 偉人の言葉 ──
  {
    id: "quote-edison-failure",
    kind: "quote",
    body: "私は失敗したことがない。うまくいかない方法を1万通り見つけただけだ。",
    source: "トーマス・エジソン",
  },
  {
    id: "quote-edison-genius",
    kind: "quote",
    body: "天才とは、1%のひらめきと99%の努力である。",
    source: "トーマス・エジソン",
  },
  {
    id: "quote-lincoln-prepare",
    kind: "quote",
    body: "準備しておこう。機会はいつか訪れるものだ。",
    source: "エイブラハム・リンカーン",
  },
  {
    id: "quote-franklin-prepare",
    kind: "quote",
    body: "準備を怠ることは、失敗の準備をすることだ。",
    source: "ベンジャミン・フランクリン",
  },
  {
    id: "quote-seneca-harbor",
    kind: "quote",
    body: "どの港をめざすか知らぬ者に、順風は吹かない。",
    source: "セネカ",
  },
  {
    id: "quote-laozi-first-step",
    kind: "quote",
    body: "千里の道も一歩から。",
    source: "老子",
  },
  {
    id: "quote-confucius-mistake",
    kind: "quote",
    body: "過ちを改めざる、これを過ちという。",
    source: "孔子",
  },
  {
    id: "quote-ninomiya-small",
    kind: "quote",
    body: "小を積みて大を為す。",
    source: "二宮尊徳",
  },
  {
    id: "quote-fukuzawa-advance",
    kind: "quote",
    body: "進まざる者は必ず退き、退かざる者は必ず進む。",
    source: "福沢諭吉",
  },
  {
    id: "quote-ryoma-own-path",
    kind: "quote",
    body: "世の人は我を何とも言わば言え、我が成す事は我のみぞ知る。",
    source: "坂本龍馬",
  },
  {
    id: "quote-einstein-new-things",
    kind: "quote",
    body: "失敗したことのない人は、新しいことに挑戦したことがない人である。",
    source: "アルベルト・アインシュタイン",
  },
] as const;
